import { gql } from '@apollo/client';

import { client } from './apollo.js';
import { normalizeAiResponse } from '../utils/aiResponse.js';

/**
 * Cliente da resposta assistida por IA.
 *
 * Decisão técnica: a chamada passa pelo GraphQL autenticado em vez de bater
 * direto no serviço de IA. Antes o bundle carregava a URL e a chave pública do
 * `ai-search`, o que expunha a credencial a quem abrisse o JS; agora a sessão
 * do usuário é a autorização e a chave fica só no servidor.
 */

const AI_SEARCH_QUERY = gql`
  query AiSearch($question: String!, $sessionId: String) {
    aiSearch(question: $question, sessionId: $sessionId) {
      specieId
      text
      relatedSpecies {
        specieId
        name
        reason
      }
    }
  }
`;

const getAiSearchSessionId = () => {
  const storageKey = 'greenbook-ai-search-session-id';

  try {
    const existingSessionId = window.sessionStorage.getItem(storageKey);
    if (existingSessionId) {
      return existingSessionId;
    }

    const sessionId = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    window.sessionStorage.setItem(storageKey, sessionId);
    return sessionId;
  } catch (error) {
    return null;
  }
};

export const fetchAiSearchResponse = async (question, { signal } = {}) => {
  if (!question || !question.trim()) {
    return null;
  }

  const { data } = await client.query({
    query: AI_SEARCH_QUERY,
    variables: { question: question.trim(), sessionId: getAiSearchSessionId() },
    // A resposta depende do estado do acervo e nunca deve vir do cache.
    fetchPolicy: 'no-cache',
    context: { fetchOptions: { signal } },
  });

  const payload = data?.aiSearch;

  if (!payload?.text) {
    return { items: [], relatedSpecies: [] };
  }

  const normalizedResponse = normalizeAiResponse(payload.text, payload.specieId);

  return {
    items: [{ text: normalizedResponse.text, specieId: normalizedResponse.specieId }],
    relatedSpecies: (payload.relatedSpecies ?? []).slice(0, 2).map((candidate) => ({
      specieId: candidate.specieId,
      name: candidate.name,
      reason: candidate.reason,
    })),
  };
};

export default fetchAiSearchResponse;
